import React, { useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Icon, ModalManagerContext, Text, Switch, Grid, Card } from '@pega/cosmos-react-core';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import { StyledTable } from '../../../Utilities/custom.styled';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { CustomModal } from '../../../Utilities/CustomModal';
import { SDKComponentProps, appState, customModalProps } from '../../../../interfaces/common';
import AddProactiveChat from '../Modals/AddProactiveChat';
import TagSnippet from '../Modals/TagSnippet';
import { updateLoaderDetails, updateBannerInfo, setChatInfo } from '../../../../redux/actions';
import { deleteProactiveChatRecord, addNewProactiveChatRecord, updateProactiveChatRecord } from '../ChatApis';

const ProactiveChat: React.FC<SDKComponentProps> = (props: SDKComponentProps) => {
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const { create } = useContext(ModalManagerContext);
  const navigate = useNavigate();
  const { platform } = props;

  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();

  const { proactiveTriggers = [], connectionId } = useSelector((state: appState) => state.chatInfo);
  const sessionId = useSelector((state: appState) => state.sessionId);

  const addProactiveChatRecord = async (
    proactiveTrigger: string,
    displayName: string,
    botCommand: string,
    isEnabled: boolean,
  ) => {
    const payload = {
      label: displayName,
      code: proactiveTrigger,
      enabled: isEnabled,
      botCommand: botCommand,
      id: '',
    };
    dispatch(updateLoaderDetails(true));
    const json = await addNewProactiveChatRecord(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));

    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('add_proactive_trigger_success'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('add_proactive_trigger_failure'), 'urgent'));
    }
  };

  const deleteChatRecord = async (chatId: string) => {
    const payload = { id: chatId };
    dispatch(updateLoaderDetails(true));
    const json = await deleteProactiveChatRecord(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));

    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('delete_proactive_trigger_success'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('delete_proactive_trigger_failure'), 'urgent'));
    }
  };

  const enableOrDisableRecord = async (chatId: string) => {
    const payload = { id: chatId };
    dispatch(updateLoaderDetails(true));
    const json = await updateProactiveChatRecord(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));

    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('update_proactive_trigger_success'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('update_proactive_trigger_failure'), 'urgent'));
    }
  };

  const data = proactiveTriggers.map((item, index) => {
    return {
      id: index,
      //   isEnabled: <Checkbox checked={item.enabled} onChange={() => enableOrDisableRecord()} />,
      isEnabled: (
        <Grid
          container={{
            cols: 'repeat(2, 38% 62%)',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Switch name="isEnabled" onChange={() => enableOrDisableRecord(item.id)} on={item.enabled} />
        </Grid>
      ),
      proactiveTrigger: <Text>{item.code}</Text>,
      displayName: <Text>{item.label}</Text>,
      botCommand: (
        <>
          <Flex container={{ direction: 'row', justify: 'between', alignItems: 'center' }}>
            <Text>{item.botCommand}</Text>
            <Button variant="simple" icon onClick={() => deleteChatRecord(item.id)}>
              <Icon name="trash" />
            </Button>
          </Flex>
        </>
      ),
    };
  });

  const columns: ColumnProps<(typeof data)[number]>[] = [
    { renderer: 'isEnabled', label: translate('enabled') },
    { renderer: 'proactiveTrigger', label: translate('proactive_trigger') },
    { renderer: 'displayName', label: translate('display_name') },
    { renderer: 'botCommand', label: translate('bot_command') },
  ];

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 1,
      }}
    >
      <Flex
        container={{
          direction: 'row',
          justify: 'between',
          alignItems: 'center',
        }}
      >
        {!platform && (
          <Button
            onClick={() => {
              modalMethods.current = create<customModalProps>(CustomModal, {
                heading: translate('copy_code_snippet_for_proactive_trigger'),
                children: <TagSnippet currentTab="proactiveChat" />,
              });
            }}
          >
            {translate('copy_code')}
          </Button>
        )}
        <Button
          onClick={() => {
            modalMethods.current = create(CustomModal, {
              heading: translate('add_trigger'),
              children: <AddProactiveChat addProactiveChatRecord={addProactiveChatRecord} />,
            });
          }}
        >
          {translate('add_trigger')}
        </Button>
      </Flex>
      <Card style={{ padding: 0 }}>
        <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={data} />
      </Card>
    </Flex>
  );
};

export default ProactiveChat;
